import React, { useState, useEffect } from 'react';
import '../styles/CustomerProfile.css';
import AddPaymentModal from '../components/modals/AddPaymentModal';
import AccountStatement from './AccountStatement';
import BalanceSheet from './BalanceSheet';
import fetchApi from '../utils/api.js';
import Swal from 'sweetalert2';
import { FaEdit, FaTrash } from 'react-icons/fa';
import SearchBar from '../components/ui/SearchBar';
import { toast } from 'react-toastify';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import AirDatePicker from '../components/ui/AirDatePicker';

const CustomerProfile = ({ customer, onBack, onPreviewInvoice, onEdit, onPreviewReturn, onEditReturn, companyId }) => {
    const { hasPermission } = usePermissionContext();
    const formatDate = (dateStr) => {
        if (!dateStr) return '---';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}-${m}-${y}`;
    };
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [paymentToEdit, setPaymentToEdit] = useState(null);
    const [totals, setTotals] = useState({ totalDebit: 0, totalCredit: 0, balance: 0 });
    const [invoices, setInvoices] = useState([]);
    const [payments, setPayments] = useState([]);
    const [challans, setChallans] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDetails, setShowDetails] = useState(false);
    const [showStatement, setShowStatement] = useState(false);

    // --- Invoice List State ---
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [invoicePage, setInvoicePage] = useState(1);
    const [invoiceEntries, setInvoiceEntries] = useState(10);

    // --- Payment List State ---
    const [paymentSearch, setPaymentSearch] = useState('');
    const [paymentPage, setPaymentPage] = useState(1);
    const [paymentEntries, setPaymentEntries] = useState(10);

    // --- Challan List State ---
    const [challanSearch, setChallanSearch] = useState('');
    const [challanPage, setChallanPage] = useState(1);
    const [challanEntries, setChallanEntries] = useState(10);

    // --- Challan Product List State ---
    const [productSearch, setProductSearch] = useState('');
    const [productPage, setProductPage] = useState(1);
    const [productEntries] = useState(10);

    // --- Date Filter State ---
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // --- Expense List State ---
    const [expenseSearch, setExpenseSearch] = useState('');
    const [expensePage, setExpensePage] = useState(1);
    const [expenseEntries, setExpenseEntries] = useState(10);

    // --- Kasar Payment List State ---
    const [kasarSearch, setKasarSearch] = useState('');
    const [kasarPage, setKasarPage] = useState(1);
    const [kasarEntries, setKasarEntries] = useState(10);

    // --- Sales Return List State ---
    const [salesReturns, setSalesReturns] = useState([]);
    const [salesReturnSearch, setSalesReturnSearch] = useState('');
    const [salesReturnPage, setSalesReturnPage] = useState(1);
    const [salesReturnEntries, setSalesReturnEntries] = useState(10);

    useEffect(() => {
        if (customer) {
            fetchCustomerData();
        }
    }, [customer]);

    const fetchCustomerData = async () => {
        try {
            setLoading(true);
            const cId = customer.id || customer.customerId || customer._id;

            let currentStart = startDate;
            let currentEnd = endDate;

            // Validation Logic
            if (currentStart && !currentEnd) {
                const today = new Date().toISOString().split('T')[0];
                currentEnd = today;
                setEndDate(today); // Sync UI
            } else if (!currentStart && currentEnd) {
                toast.warn('Start Date is required!');
                setLoading(false);
                return;
            }

            let queryParams = `?customerId=${cId}`;
            if (currentStart && currentEnd) {
                queryParams += `&startDate=${currentStart}&endDate=${currentEnd}`;
            }

            // Fetch Totals
            if (hasPermission('Sales Invoice', 'view')) {
                try {
                    const totalsData = await fetchApi(`/invoice-payment/totals/${cId}${currentStart && currentEnd ? `?startDate=${currentStart}&endDate=${currentEnd}` : ''}`);
                    setTotals(totalsData);
                } catch (err) { console.warn("Failed to fetch totals", err); }

                // Fetch Invoices
                try {
                    const ledgerData = await fetchApi(`/invoice-payment/ledger/${cId}${currentStart && currentEnd ? `?startDate=${currentStart}&endDate=${currentEnd}` : ''}`);

                    const invList = ledgerData.filter(e => e.type === 'Invoice').map(e => ({
                        id: e.id,
                        invoiceNo: e.refNo,
                        date: e.date,
                        place: customer.customerCity || customer.city || '---',
                        amount: e.debit.toFixed(2)
                    }));
                    setInvoices(invList);
                } catch (err) { console.warn("Failed to fetch ledger", err); }
            } else { setTotals({ totalInvoiceAmount: 0, paidAmount: 0, pendingAmount: 0 }); setInvoices([]); }

            // Fetch Payments list separately
            if (hasPermission('Sales Invoice', 'view')) {
                try {
                    const paymentsData = await fetchApi(`/invoice-payment${queryParams}`);
                    setPayments(paymentsData);
                } catch (err) { console.warn("Failed to fetch payments", err); }
            } else { setPayments([]); }

            // Fetch Challans - Get all pending ones
            if (hasPermission('Sales Challan', 'view')) {
                try {
                    const challansData = await fetchApi(`/sales-challan?customerId=${cId}&status=Pending&limit=1000`);
                    setChallans(challansData.salesChallans || (Array.isArray(challansData) ? challansData : []));
                } catch (err) { console.warn("Failed to fetch challans", err); }
            } else { setChallans([]); }

            // Fetch Expenses
            if (hasPermission('Expense', 'view')) {
                try {
                    const expensesData = await fetchApi(`/expense?partyType=customer&partyId=${cId}&limit=1000${currentStart && currentEnd ? `&startDate=${currentStart}&endDate=${currentEnd}` : ''}`);
                    setExpenses(expensesData.expenses || []);
                } catch (err) { console.warn("Failed to fetch expenses", err); }
            } else { setExpenses([]); }

            // Fetch Sales Returns
            if (hasPermission('SalesReturn', 'view')) {
                try {
                    const returnsData = await fetchApi(`/sales-return?customerId=${cId}&limit=1000${currentStart && currentEnd ? `&startDate=${currentStart}&endDate=${currentEnd}` : ''}`);
                    setSalesReturns(returnsData.salesReturns || (Array.isArray(returnsData) ? returnsData : []));
                } catch (err) { console.warn("Failed to fetch returns", err); }
            } else { setSalesReturns([]); }

        } catch (err) {
            console.error("Error fetching customer data:", err);
        } finally {
            setLoading(false);
        }
    };

    // --- Helper Functions ---
    const filterData = (list, search, keys) => {
        if (!search) return list;
        return list.filter(item =>
            keys.some(key => item[key]?.toString().toLowerCase().includes(search.toLowerCase()))
        );
    };

    // Unified data mapping to support multiple naming conventions (backend and mocks)
    const displayData = {
        name: customer?.customerName || customer?.customer_name || customer?.name || '---',
        tradeName: customer?.customerTradeName || customer?.customer_trade_name || customer?.tradeName || '---',
        referenceName: customer?.customerReferenceName || customer?.customer_reference_name || customer?.referenceName || '---',
        email: customer?.customerEmail || customer?.customer_email || customer?.email || '---',
        mobileNumber: customer?.customerMobileNumber || customer?.customer_mobile || customer?.mobileNumber || customer?.mobile || '---',
        gst: customer?.customerGst || customer?.customer_gst || customer?.gst || '---',
        panNo: customer?.customerPanNo || customer?.customer_pan_no || customer?.panNo || customer?.pan || '---',
        aadhaarNo: customer?.customerAadhaarNo || customer?.customer_aadhaar_no || customer?.aadhaarNo || customer?.aadhaar || '---',
        customerType: customer?.customerType || customer?.CustomerType?.customer_type_name || '---',
        saleType: customer?.customerSaleType || customer?.saleType || customer?.SaleType?.sale_type_name || '---',
        country: customer?.customerCountry || customer?.customer_country || customer?.country || 'India',
        state: customer?.customerState || customer?.customer_state || customer?.state || '---',
        city: customer?.customerCity || customer?.customer_city || customer?.city || '---',
        pinCode: customer?.customerPinCode || customer?.customer_pin_code || customer?.pinCode || '---',
        stateCode: customer?.customerStateCode || customer?.customer_state_code || customer?.stateCode || '---',
        address: customer?.customerAddress || customer?.customer_address || customer?.address || '---'
    };

    // Filter Invoices
    const filteredInvoices = filterData(invoices, invoiceSearch, ['id', 'date', 'place', 'amount']);
    const invoiceLastIdx = invoicePage * invoiceEntries;
    const invoiceFirstIdx = invoiceLastIdx - invoiceEntries;
    const currentInvoices = filteredInvoices.slice(invoiceFirstIdx, invoiceLastIdx);

    // Filter Payments
    const filteredPayments = filterData(payments, paymentSearch, ['paymentAmount', 'paymentModeName', 'paymentCollectorName']);
    const paymentLastIdx = paymentPage * paymentEntries;
    const paymentFirstIdx = paymentLastIdx - paymentEntries;
    const currentPayments = filteredPayments.slice(paymentFirstIdx, paymentLastIdx);

    // Filter Expenses
    const filteredExpenses = filterData(expenses, expenseSearch, ['expenseName', 'invoiceNo', 'expensePurpose', 'date', 'expenseTypeName']);
    const expenseLastIdx = expensePage * expenseEntries;
    const expenseFirstIdx = expenseLastIdx - expenseEntries;
    const currentExpenses = filteredExpenses.slice(expenseFirstIdx, expenseLastIdx);

    // Filter Kasar Payments (where kasarAmount > 0)
    const kasarPayments = React.useMemo(() => {
        return payments.filter(pay => Number(pay.kasarAmount || pay.kasar_amount || 0) > 0);
    }, [payments]);

    const filteredKasarPayments = filterData(kasarPayments, kasarSearch, ['kasarAmount', 'paymentAmount', 'paymentModeName', 'remark']);
    const kasarLastIdx = kasarPage * kasarEntries;
    const kasarFirstIdx = kasarLastIdx - kasarEntries;
    const currentKasarPayments = filteredKasarPayments.slice(kasarFirstIdx, kasarLastIdx);

    // Filter Sales Returns
    const filteredSalesReturns = React.useMemo(() => {
        return salesReturns.filter(ret => {
            const searchLower = salesReturnSearch.toLowerCase();
            return (
                (ret.return_no && ret.return_no.toLowerCase().includes(searchLower)) ||
                (ret.original_invoice_no && ret.original_invoice_no.toLowerCase().includes(searchLower)) ||
                (ret.note && ret.note.toLowerCase().includes(searchLower)) ||
                (ret.total_amount && String(ret.total_amount).includes(searchLower))
            );
        });
    }, [salesReturns, salesReturnSearch]);

    const salesReturnLastIdx = salesReturnPage * salesReturnEntries;
    const salesReturnFirstIdx = salesReturnLastIdx - salesReturnEntries;
    const currentSalesReturns = filteredSalesReturns.slice(salesReturnFirstIdx, salesReturnLastIdx);

    const flattenedChallanItems = React.useMemo(() => {
        const items = [];
        challans.forEach(ch => {
            let chItems = [];
            try {
                chItems = typeof ch.items === 'string' ? JSON.parse(ch.items) : ch.items;
            } catch (e) { console.error(e); }

            if (Array.isArray(chItems)) {
                chItems.forEach((it, idx) => {
                    items.push({
                        ...it,
                        uniqueId: `${ch.id || ch._id}-${idx}`,
                        challanId: ch.id || ch._id,
                        challanNo: ch.invoiceNo || ch.challan_no || ch.challanNo,
                        challanDate: ch.invoiceDate || ch.challan_date || ch.challanDate || ch.date,
                        challanName: ch.invoiceName || ch.challan_name || ch.challanName,
                        fullChallan: ch,
                        date: ch.invoiceDate || ch.challan_date || ch.challanDate || ch.date,
                        productName: it.product || it.productName || it.item_name || '---',
                        partNumber: it.hsnCode || it.productHsnCode || it.partNumber || it.hsn_code || '---',
                        qty: it.quantity || it.qty || 0
                    });
                });
            }
        });
        return items;
    }, [challans]);

    // Filter & Format Challans Summary (One row per Challan) - to match Seller Profile
    const challanSummaryList = React.useMemo(() => {
        return challans.map(ch => {
            let chItems = [];
            try {
                chItems = typeof ch.items === 'string' ? JSON.parse(ch.items) : (ch.items || []);
            } catch (e) { console.error(e); }

            const totalQty = chItems.reduce((sum, it) => sum + (Number(it.quantity || it.qty) || 0), 0);

            return {
                id: ch.id || ch._id,
                challanNo: ch.invoiceNo || ch.challan_no || ch.challanNo,
                date: ch.invoiceDate || ch.challan_date || ch.challanDate || ch.date,
                totalItems: chItems.length,
                totalQty: totalQty,
                fullChallan: ch,
                items: chItems
            };
        });
    }, [challans]);

    const filteredChallanSummary = filterData(challanSummaryList, challanSearch, ['challanNo', 'date']);
    const challanLastIdx = challanPage * challanEntries;
    const challanFirstIdx = challanLastIdx - challanEntries;
    const currentChallans = filteredChallanSummary.slice(challanFirstIdx, challanLastIdx);

    // Flatten all pending challan items into individual product rows for the product table
    const flattenedChallanProducts = React.useMemo(() => {
        const products = [];
        challans.forEach(ch => {
            let chItems = [];
            try {
                chItems = typeof ch.items === 'string' ? JSON.parse(ch.items) : (ch.items || []);
            } catch (e) { console.error(e); }

            if (Array.isArray(chItems)) {
                chItems.forEach((it, idx) => {
                    if (!it) return;
                    products.push({
                        uniqueId: `${ch.id || ch._id}-${idx}`,
                        challanId: ch.id || ch._id,
                        challanNo: ch.challan_no || ch.invoiceNo || ch.challanNo,
                        date: ch.challan_date || ch.invoiceDate || ch.challanDate || ch.date,
                        productName: it.product || it.productName || it.item_name || '---',
                        category: it.category || '',
                        hsnCode: it.hsnCode || it.productHsnCode || it.partNumber || it.hsn_code || '---',
                        grade: it.grade || '',
                        color: it.color || '',
                        unit: it.unit || '',
                        quantity: it.quantity || it.qty || 0,
                        rate: it.rate || 0,
                        total: it.total || 0,
                        fullChallan: ch,
                        rawItem: it
                    });
                });
            }
        });
        return products;
    }, [challans]);

    const filteredProducts = filterData(flattenedChallanProducts, productSearch, ['productName', 'challanNo', 'category', 'grade']);
    const productLastIdx = productPage * productEntries;
    const productFirstIdx = productLastIdx - productEntries;
    const currentProducts = filteredProducts.slice(productFirstIdx, productLastIdx);

    const handleConvertSingleRow = (row) => {
        const detail = {
            ...row.fullChallan,
            isFromChallan: true,
            challanId: row.fullChallan.id || row.fullChallan._id,
            invoiceName: 'AW',
            invoiceNo: ''
        };
        const event = new CustomEvent('convertSalesChallansToInvoice', { detail });
        window.dispatchEvent(event);
    };

    const handleBatchConvertChallans = () => {
        if (challans.length === 0) {
            toast.info("No pending challans to convert");
            return;
        }

        const base = challans[0];
        const allItems = [];
        challans.forEach(ch => {
            let chItems = [];
            try {
                chItems = typeof ch.items === 'string' ? JSON.parse(ch.items) : ch.items;
            } catch (e) { }
            if (Array.isArray(chItems)) {
                allItems.push(...chItems);
            }
        });

        const mergedData = {
            ...base,
            invoiceName: 'AW',
            invoiceNo: '',
            items: allItems,
            isFromChallan: true,
            challanId: base.id || base._id,
            sourceChallanIds: challans.map(ch => ch.id || ch._id)
        };

        const event = new CustomEvent('convertSalesChallansToInvoice', { detail: mergedData });
        window.dispatchEvent(event);
    };

    // Convert ALL flattened products from the product list
    const handleConvertAllProducts = () => {
        if (filteredProducts.length === 0) {
            toast.info("No products available to convert");
            return;
        }

        const sourceChallanIds = [...new Set(filteredProducts.map(p => p.challanId))];
        const allItems = filteredProducts.map(p => ({
            ...p.rawItem,
            product: p.productName,
            category: p.category,
            hsnCode: p.hsnCode,
            grade: p.grade,
            color: p.color,
            unit: p.unit,
            quantity: p.quantity,
            rate: p.rate,
            total: p.total,
            sourceChallanId: p.challanId
        }));

        const base = filteredProducts[0].fullChallan;
        const mergedData = {
            ...base,
            invoiceName: 'AW',
            invoiceNo: '',
            items: allItems,
            isBatchConversion: true,
            isFromChallan: true,
            challanId: base.id || base._id,
            sourceChallanIds: sourceChallanIds
        };

        const event = new CustomEvent('convertSalesChallansToInvoice', { detail: mergedData });
        window.dispatchEvent(event);
    };

    const handleDeletePayment = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await fetchApi(`/invoice-payment/${id}`, { method: 'DELETE' });
                Swal.fire('Deleted!', 'Payment has been deleted.', 'success');
                fetchCustomerData();
            } catch (error) {
                console.error("Delete Error:", error);
                Swal.fire('Error!', 'Failed to delete payment.', 'error');
            }
        }
    };

    const handleDeleteSalesReturn = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Delete this sales return? This will also revert stock adjustments.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await fetchApi(`/sales-return/${id}`, { method: 'DELETE' });
                Swal.fire('Deleted!', 'Sales return has been deleted.', 'success');
                fetchCustomerData();
            } catch (error) {
                console.error("Delete Error:", error);
                Swal.fire('Error!', 'Failed to delete sales return.', 'error');
            }
        }
    };

    const handleEditPayment = (payment) => {
        setPaymentToEdit(payment);
        setIsPaymentModalOpen(true);
    };

    const handlePreviewInvoiceClick = async (inv) => {
        try {
            // inv.id is the database _id from ledger
            const fullInvoice = await fetchApi(`/sales-invoice/${inv.id}`);
            if (fullInvoice && onPreviewInvoice) {
                onPreviewInvoice(fullInvoice);
            }
        } catch (error) {
            console.error("Error fetching full invoice for preview:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch invoice details for preview.'
            });
        }
    };

    const [showBalanceSheet, setShowBalanceSheet] = useState(false);

    if (showStatement) {
        return (
            <AccountStatement
                customer={displayData}
                customerId={customer.id || customer.customerId || customer._id}
                startDate={startDate}
                endDate={endDate}
                onBack={() => setShowStatement(false)}
            />
        );
    }

    if (showBalanceSheet) {
        return (
            <BalanceSheet
                customer={displayData}
                customerId={customer.id || customer.customerId || customer._id}
                startDate={startDate}
                endDate={endDate}
                onBack={() => setShowBalanceSheet(false)}
                companyId={companyId}
            />
        );
    }

    return (
        <div className="customer-profile-container">
            <div className="customer-profile-header">
                <h2 className="customer-profile-title">
                    {(displayData.tradeName && displayData.tradeName !== '---') ? displayData.tradeName : (displayData.name || 'Customer Profile')}
                </h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    {hasPermission('Customer', 'update') && onEdit && (
                        <button className="customer-profile-back-btn" onClick={onEdit} style={{ backgroundColor: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <FaEdit /> Edit
                        </button>
                    )}
                    <button
                        className="customer-profile-back-btn"
                        onClick={() => setShowDetails(!showDetails)}
                        style={{ backgroundColor: showDetails ? '#ef4444' : '#10b981', color: 'white' }}
                    >
                        {showDetails ? 'Closed Details' : 'Open Details'}
                    </button>
                    {onBack && (
                        <button className="customer-profile-back-btn" onClick={onBack}>
                            ← Back
                        </button>
                    )}
                </div>
            </div>

            {/* Date Filter Section (Red box from user image) */}
            <div className="profile-date-filter-row">
                <div className="date-input-group">
                    <label>From Date:</label>
                    <AirDatePicker
                        value={startDate}
                        onChange={(dateStr) => setStartDate(dateStr)}
                    />
                </div>
                <div className="date-input-group">
                    <label>To Date:</label>
                    <AirDatePicker
                        value={endDate}
                        onChange={(dateStr) => setEndDate(dateStr)}
                    />
                </div>
                <div className="filter-actions-group">
                    <button className="btn-filter-apply" onClick={fetchCustomerData}>Apply Filter</button>
                    <button className="btn-filter-clear" onClick={() => { setStartDate(''); setEndDate(''); fetchCustomerData(); }}>Clear</button>
                </div>
            </div>

            {/* Top Details Section */}
            {showDetails && (
                <div className="customer-profile-details-grid">
                    {/* Personal & Business Details */}
                    <div className="customer-detail-card">
                        <div className="customer-detail-card-title">Personal & Business Details</div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">Trade Name:</span>
                            <span className="customer-detail-value">{displayData.tradeName}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">Customer Name:</span>
                            <span className="customer-detail-value">{displayData.name}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">Reference Name:</span>
                            <span className="customer-detail-value">{displayData.referenceName}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">Email:</span>
                            <span className="customer-detail-value">{displayData.email}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">Mobile:</span>
                            <span className="customer-detail-value">{displayData.mobileNumber}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">GST:</span>
                            <span className="customer-detail-value">{displayData.gst}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">PAN No:</span>
                            <span className="customer-detail-value">{displayData.panNo}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">Aadhaar Card No:</span>
                            <span className="customer-detail-value">{displayData.aadhaarNo}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">Customer Type:</span>
                            <span className="customer-detail-value">{displayData.customerType}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">Sale Type:</span>
                            <span className="customer-detail-value">{displayData.saleType}</span>
                        </div>
                    </div>

                    {/* Address Details */}
                    <div className="customer-detail-card">
                        <div className="customer-detail-card-title">Address Details</div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">Country:</span>
                            <span className="customer-detail-value">{displayData.country}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">State:</span>
                            <span className="customer-detail-value">{displayData.state}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">City:</span>
                            <span className="customer-detail-value">{displayData.city}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">Pin Code:</span>
                            <span className="customer-detail-value">{displayData.pinCode}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">State Code:</span>
                            <span className="customer-detail-value">{displayData.stateCode}</span>
                        </div>
                        <div className="customer-detail-row">
                            <span className="customer-detail-label">Address:</span>
                            <span className="customer-detail-value" style={{ maxWidth: '60%' }}>{displayData.address}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Statistics Row */}
            <div className="customer-stats-row">
                <div className="customer-stat-card">
                    <div className="customer-stat-icon-wrapper stat-green">
                        📄
                    </div>
                    <div className="customer-stat-info">
                        <h3>{(totals.totalCredit || 0).toFixed(2)}/-</h3>
                        <p className="customer-stat-label">Credit</p>
                    </div>
                </div>
                <div className="customer-stat-card">
                    <div className="customer-stat-icon-wrapper stat-blue">
                        💵
                    </div>
                    <div className="customer-stat-info">
                        <h3>{(totals.totalDebit || 0).toFixed(2)}/-</h3>
                        <p className="customer-stat-label">Debit</p>
                    </div>
                </div>
                <div className="customer-stat-card">
                    <div className="customer-stat-icon-wrapper stat-red">
                        📱
                    </div>
                    <div className="customer-stat-info">
                        <h3>{(totals.balance || 0).toFixed(2)}/-</h3>
                        <p className="customer-stat-label">
                            {totals.balance > 0 ? 'Pending' : totals.balance < 0 ? 'Advance' : 'Balance'}
                        </p>
                    </div>
                </div>

                <div className="customer-profile-actions">
                    <button className="customer-action-btn" onClick={() => setIsPaymentModalOpen(true)}>Add Payment</button>
                    <button className="customer-action-btn" onClick={() => setShowBalanceSheet(true)}>Balance Sheet</button>
                    <button className="customer-action-btn" onClick={() => setShowStatement(true)}>Account Statement</button>
                </div>
            </div>

            {/* Tables Area */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div className="customer-profile-tables-grid full-width" style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%' }}>
                {/* Sales Challan Invoice List */}
                {hasPermission('Sales Challan', 'view') && (
                <div className="customer-list-section full-width">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="customer-list-header" style={{ margin: 0, fontWeight: '600', fontSize: '18px' }}>Sales Challan Invoice List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={challanSearch}
                                onChange={(e) => {
                                    setChallanSearch(e.target.value);
                                    setChallanPage(1);
                                }}
                                placeholder="Search challan items..."
                            />
                            <button className="btn-batch-convert" onClick={handleBatchConvertChallans} style={{ position: 'static', backgroundColor: '#059669', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>
                                Sales Challan To Sales Invoice
                            </button>
                        </div>
                    </div>

                    <table className="customer-profile-table">
                        <thead>
                            <tr>
                                <th>CHALLAN NO</th>
                                <th>DATE</th>
                                <th>TOTAL ITEMS</th>
                                <th>TOTAL QTY</th>
                                <th style={{ width: '100px' }}>ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentChallans.length > 0 ? currentChallans.map((ch, idx) => (
                                <tr key={ch.id || idx}>
                                    <td style={{ fontWeight: '500', color: '#1e3a8a' }}>{ch.challanNo}</td>
                                    <td>{formatDate(ch.date)}</td>
                                    <td>{ch.totalItems}</td>
                                    <td>{parseFloat(ch.totalQty || 0).toFixed(2)}/-</td>
                                    <td>
                                        <button
                                            className="btn-row-convert"
                                            title="Convert this challan"
                                            onClick={() => handleConvertSingleRow(ch)}
                                        >
                                            Convert
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={5} className="no-data">No data available in table</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-info">
                            Showing {filteredChallanSummary.length === 0 ? 0 : challanFirstIdx + 1} to {Math.min(challanLastIdx, filteredChallanSummary.length)} of {filteredChallanSummary.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={challanPage <= 1}
                                onClick={() => setChallanPage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredChallanSummary.length / challanEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === challanPage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setChallanPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={challanPage >= Math.ceil(filteredChallanSummary.length / challanEntries)}
                                onClick={() => setChallanPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {/* Sales Challan Product List (New Table) */}
                {hasPermission('Sales Challan', 'view') && (
                <div className="customer-list-section full-width">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="customer-list-header" style={{ margin: 0, fontWeight: '600', fontSize: '18px' }}>Sales Challan Product List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={productSearch}
                                onChange={(e) => {
                                    setProductSearch(e.target.value);
                                    setProductPage(1);
                                }}
                                placeholder="Search products..."
                            />
                            <button className="btn-batch-convert" onClick={handleConvertAllProducts} style={{ position: 'static', backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>
                                Convert All Products To Invoice
                            </button>
                        </div>
                    </div>

                    <table className="customer-profile-table">
                        <thead>
                            <tr>
                                <th>CHALLAN NO</th>
                                <th>PRODUCT NAME</th>
                                <th>CATEGORY</th>
                                <th>GRADE</th>
                                <th>COLOR</th>
                                <th>QTY</th>
                                <th>RATE</th>
                                <th>TOTAL</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentProducts.length > 0 ? currentProducts.map((p, idx) => (
                                <tr key={p.uniqueId || idx}>
                                    <td style={{ fontWeight: '500', color: '#1e3a8a' }}>{p.challanNo}</td>
                                    <td style={{ fontWeight: '500' }}>{p.productName}</td>
                                    <td>{p.category || '---'}</td>
                                    <td>{p.grade || '---'}</td>
                                    <td>{p.color || '---'}</td>
                                    <td>{parseFloat(p.quantity || 0).toFixed(2)}</td>
                                    <td>₹{parseFloat(p.rate || 0).toFixed(2)}</td>
                                    <td>₹{parseFloat(p.total || 0).toFixed(2)}</td>
                                </tr>
                            )) : (
                                <tr><td colSpan={8} className="no-data">No pending products available</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-info">
                            Showing {filteredProducts.length === 0 ? 0 : productFirstIdx + 1} to {Math.min(productLastIdx, filteredProducts.length)} of {filteredProducts.length} entries
                        </div>
                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={productPage <= 1}
                                onClick={() => setProductPage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredProducts.length / productEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === productPage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setProductPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={productPage >= Math.ceil(filteredProducts.length / productEntries)}
                                onClick={() => setProductPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}
            </div>

            <div className="customer-profile-tables-grid">
                {/* Invoice List */}
                {hasPermission('Sales Invoice', 'view') && (
                <div className="customer-list-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="customer-list-header" style={{ margin: 0 }}>Invoice List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={invoiceSearch}
                                onChange={(e) => {
                                    setInvoiceSearch(e.target.value);
                                    setInvoicePage(1);
                                }}
                                placeholder="Search invoices..."
                            />
                        </div>
                    </div>

                    <table className="customer-profile-table">
                        <thead>
                            <tr>
                                <th>INVOICE NO</th>
                                <th>INVOICE DATE</th>
                                <th>PLACE OF SUPPLY</th>
                                <th>TOTAL AMOUNT</th>
                                <th>PREVIEW</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentInvoices.length > 0 ? currentInvoices.map(inv => (
                                <tr key={inv.id}>
                                    <td>{inv.invoiceNo}</td>
                                    <td>{inv.date}</td>
                                    <td>{inv.place}</td>
                                    <td>{inv.amount}</td>
                                    <td>
                                        <button
                                            className="list-page-icon-btn"
                                            title="Preview"
                                            onClick={() => handlePreviewInvoiceClick(inv)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                <circle cx="12" cy="12" r="3"></circle>
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={6} className="no-data">No data available in table</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-entries">
                            <span>Show</span>
                            <select
                                className="list-page-entries-select"
                                value={invoiceEntries}
                                onChange={(e) => {
                                    setInvoiceEntries(Number(e.target.value));
                                    setInvoicePage(1);
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                        </div>

                        <div className="list-page-info">
                            Showing {filteredInvoices.length === 0 ? 0 : invoiceFirstIdx + 1} to {Math.min(invoiceLastIdx, filteredInvoices.length)} of {filteredInvoices.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={invoicePage <= 1}
                                onClick={() => setInvoicePage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredInvoices.length / invoiceEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === invoicePage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setInvoicePage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={invoicePage >= Math.ceil(filteredInvoices.length / invoiceEntries)}
                                onClick={() => setInvoicePage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {/* Payment List */}
                {hasPermission('Sales Invoice', 'view') && (
                <div className="customer-list-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="customer-list-header" style={{ margin: 0 }}>Payment List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={paymentSearch}
                                onChange={(e) => {
                                    setPaymentSearch(e.target.value);
                                    setPaymentPage(1);
                                }}
                                placeholder="Search payments..."
                            />
                        </div>
                    </div>

                    <table className="customer-profile-table">
                        <thead>
                            <tr>
                                <th>AMOUNT</th>
                                <th>DATE TIME</th>
                                <th>MODE</th>
                                <th>COLLECTOR</th>
                                <th>ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentPayments.length > 0 ? currentPayments.map((pay, idx) => (
                                <tr key={idx}>
                                    <td>{Number(pay.paymentAmount).toFixed(2)}</td>
                                    <td>{pay.paymentDate} {pay.paymentTime}</td>
                                    <td>{pay.paymentModeName}</td>
                                    <td>{pay.paymentCollectorName || 'Authorized'}</td>
                                    <td>
                                        <div className="list-page-action-icons">
                                            <button className="list-page-icon-btn" title="Edit" onClick={() => handleEditPayment(pay)}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                </svg>
                                            </button>
                                            <button className="list-page-icon-btn" title="Delete" onClick={() => handleDeletePayment(pay._id)}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={5} className="no-data">No data available in table</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-entries">
                            <span>Show</span>
                            <select
                                className="list-page-entries-select"
                                value={paymentEntries}
                                onChange={(e) => {
                                    setPaymentEntries(Number(e.target.value));
                                    setPaymentPage(1);
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                        </div>

                        <div className="list-page-info">
                            Showing {filteredPayments.length === 0 ? 0 : paymentFirstIdx + 1} to {Math.min(paymentLastIdx, filteredPayments.length)} of {filteredPayments.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={paymentPage <= 1}
                                onClick={() => setPaymentPage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredPayments.length / paymentEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === paymentPage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setPaymentPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={paymentPage >= Math.ceil(filteredPayments.length / paymentEntries)}
                                onClick={() => setPaymentPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {/* Kasar Payment List */}
                {hasPermission('Sales Invoice', 'view') && (
                <div className="customer-list-section full-width" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="customer-list-header" style={{ margin: 0, fontWeight: '600', fontSize: '18px' }}>Kasar Payment List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={kasarSearch}
                                onChange={(e) => {
                                    setKasarSearch(e.target.value);
                                    setKasarPage(1);
                                }}
                                placeholder="Search kasar payments..."
                            />
                        </div>
                    </div>

                    <table className="customer-profile-table">
                        <thead>
                            <tr>
                                <th>KASAR AMOUNT</th>
                                <th>PAYMENT AMOUNT</th>
                                <th>DATE TIME</th>
                                <th>MODE</th>
                                <th>REMARK</th>
                                <th>ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentKasarPayments.length > 0 ? currentKasarPayments.map((pay, idx) => (
                                <tr key={idx}>
                                    <td style={{ fontWeight: '600', color: '#dc2626' }}>₹{Number(pay.kasarAmount || pay.kasar_amount || 0).toFixed(2)}</td>
                                    <td>₹{Number(pay.paymentAmount).toFixed(2)}</td>
                                    <td>{pay.paymentDate} {pay.paymentTime}</td>
                                    <td>{pay.paymentModeName}</td>
                                    <td>{pay.remark || '---'}</td>
                                    <td>
                                        <div className="list-page-action-icons">
                                            {hasPermission('Sales Invoice', 'update') && (
                                                <button className="list-page-icon-btn" title="Edit" onClick={() => handleEditPayment(pay)}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                    </svg>
                                                </button>
                                            )}
                                            {hasPermission('Sales Invoice', 'delete') && (
                                                <button className="list-page-icon-btn" title="Delete" onClick={() => handleDeletePayment(pay._id)}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={6} className="no-data">No data available in table</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-entries">
                            <span>Show</span>
                            <select
                                className="list-page-entries-select"
                                value={kasarEntries}
                                onChange={(e) => {
                                    setKasarEntries(Number(e.target.value));
                                    setKasarPage(1);
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                        </div>

                        <div className="list-page-info">
                            Showing {filteredKasarPayments.length === 0 ? 0 : kasarFirstIdx + 1} to {Math.min(kasarLastIdx, filteredKasarPayments.length)} of {filteredKasarPayments.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={kasarPage <= 1}
                                onClick={() => setKasarPage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredKasarPayments.length / kasarEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === kasarPage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setKasarPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={kasarPage >= Math.ceil(filteredKasarPayments.length / kasarEntries)}
                                onClick={() => setKasarPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {/* Sales Return List */}
                {hasPermission('SalesReturn', 'view') && (
                <div className="customer-list-section full-width" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="customer-list-header" style={{ margin: 0, fontWeight: '600', fontSize: '18px' }}>Sales Return List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={salesReturnSearch}
                                onChange={(e) => {
                                    setSalesReturnSearch(e.target.value);
                                    setSalesReturnPage(1);
                                }}
                                placeholder="Search sales returns..."
                            />
                        </div>
                    </div>

                    <table className="customer-profile-table">
                        <thead>
                            <tr>
                                <th>RETURN NO</th>
                                <th>RETURN DATE</th>
                                <th>INVOICE NO</th>
                                <th>TAXABLE AMOUNT</th>
                                <th>TOTAL AMOUNT</th>
                                <th>NOTE</th>
                                <th>ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentSalesReturns.length > 0 ? currentSalesReturns.map((ret, idx) => (
                                <tr key={ret._id || ret.id || idx}>
                                    <td style={{ fontWeight: '600', color: '#0c3447' }}>{ret.return_no}</td>
                                    <td>{formatDate(ret.return_date)}</td>
                                    <td>{ret.original_invoice_no || '---'}</td>
                                    <td>₹{parseFloat(ret.taxable_amount || 0).toFixed(2)}</td>
                                    <td style={{ fontWeight: '600' }}>₹{parseFloat(ret.total_amount || 0).toFixed(2)}</td>
                                    <td>{ret.note || '---'}</td>
                                    <td>
                                        <div className="list-page-action-icons">
                                            <button
                                                className="list-page-icon-btn"
                                                title="Preview"
                                                onClick={() => onPreviewReturn && onPreviewReturn(ret)}
                                                style={{ marginRight: '8px' }}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                    <circle cx="12" cy="12" r="3"></circle>
                                                </svg>
                                            </button>
                                            <button
                                                className="list-page-icon-btn"
                                                title="Edit"
                                                onClick={() => onEditReturn && onEditReturn(ret)}
                                                style={{ marginRight: '8px' }}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                    <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                </svg>
                                            </button>
                                            <button
                                                className="list-page-icon-btn"
                                                title="Delete"
                                                onClick={() => handleDeleteSalesReturn(ret._id || ret.id)}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={7} className="no-data">No sales returns available</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-entries">
                            <span>Show</span>
                            <select
                                className="list-page-entries-select"
                                value={salesReturnEntries}
                                onChange={(e) => {
                                    setSalesReturnEntries(Number(e.target.value));
                                    setSalesReturnPage(1);
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                        </div>

                        <div className="list-page-info">
                            Showing {filteredSalesReturns.length === 0 ? 0 : salesReturnFirstIdx + 1} to {Math.min(salesReturnLastIdx, filteredSalesReturns.length)} of {filteredSalesReturns.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={salesReturnPage <= 1}
                                onClick={() => setSalesReturnPage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredSalesReturns.length / salesReturnEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === salesReturnPage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setSalesReturnPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={salesReturnPage >= Math.ceil(filteredSalesReturns.length / salesReturnEntries)}
                                onClick={() => setSalesReturnPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {/* Expense List */}
                {hasPermission('Expense', 'view') && (
                <div className="customer-list-section full-width" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="customer-list-header" style={{ margin: 0, fontWeight: '600', fontSize: '18px' }}>Expense List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={expenseSearch}
                                onChange={(e) => {
                                    setExpenseSearch(e.target.value);
                                    setExpensePage(1);
                                }}
                                placeholder="Search expenses..."
                            />
                        </div>
                    </div>

                    <table className="customer-profile-table">
                        <thead>
                            <tr>
                                <th>EXPENSE TYPE</th>
                                <th>DATE</th>
                                <th>EXPENSE NAME</th>
                                <th>INVOICE NO</th>
                                <th>NET AMOUNT</th>
                                <th>GST AMOUNT</th>
                                <th>TOTAL AMOUNT</th>
                                <th>PURPOSE</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentExpenses.length > 0 ? currentExpenses.map((exp, idx) => (
                                <tr key={exp.id || idx}>
                                    <td style={{ fontWeight: '500', color: '#1e3a8a' }}>{exp.expenseTypeName || '---'}</td>
                                    <td>{formatDate(exp.date)}</td>
                                    <td style={{ fontWeight: '500' }}>{exp.expenseName || '---'}</td>
                                    <td>{exp.invoiceNo || '---'}</td>
                                    <td>₹{parseFloat(exp.netAmount || 0).toFixed(2)}</td>
                                    <td>₹{parseFloat(exp.gstAmount || 0).toFixed(2)}</td>
                                    <td style={{ fontWeight: '600' }}>₹{parseFloat(exp.rowTotal || 0).toFixed(2)}</td>
                                    <td>{exp.expensePurpose || '---'}</td>
                                </tr>
                            )) : (
                                <tr><td colSpan={8} className="no-data">No data available in table</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-entries">
                            <span>Show</span>
                            <select
                                className="list-page-entries-select"
                                value={expenseEntries}
                                onChange={(e) => {
                                    setExpenseEntries(Number(e.target.value));
                                    setExpensePage(1);
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                        </div>

                        <div className="list-page-info">
                            Showing {filteredExpenses.length === 0 ? 0 : expenseFirstIdx + 1} to {Math.min(expenseLastIdx, filteredExpenses.length)} of {filteredExpenses.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={expensePage <= 1}
                                onClick={() => setExpensePage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredExpenses.length / expenseEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === expensePage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setExpensePage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={expensePage >= Math.ceil(filteredExpenses.length / expenseEntries)}
                                onClick={() => setExpensePage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

            </div>
            </div>

            {/* Add Payment Modal */}
            <AddPaymentModal
                isOpen={isPaymentModalOpen}
                onClose={() => {
                    setIsPaymentModalOpen(false);
                    setPaymentToEdit(null);
                    fetchCustomerData();
                }}
                entityName={displayData.name}
                entityType="Customer"
                entityId={customer?.id || customer?.customerId || customer?._id}
                paymentToEdit={paymentToEdit}
            />
        </div>
    );
};

export default CustomerProfile;
